// İSG Portal — Firma Onay-Routing ayarı (belge türü → onaylayan departman[lar]) API + tipleri.
// **İSG uzmanı tarafı** (web-expert). Firma, her belge türü için Kontrol aşamasında hangi
// departman(lar)ın onaylayacağını + eskalasyon süresi/departmanını tanımlar (SPEC §8).
//
// KAYNAK: api-contract.md **§31.2**. Belge türü kimliği = `(entity, code)` — §22 `DocumentTypeRef`
// ile aynı şekil (#7: `appliesTo`/`documentType` yerine; `entity` ∈ `personnel`/`vehicle`, küçük
// harf; `code` = o entity'nin §27 kayıt kodu). Seçenekler §27 kaydından (`GET /document-types`).
//
// **Yetki (§31.2):** uçlar `[ModuleAction("permit-types", …)]` taşır — KKD/Kontrol katalog CRUD'u
// ile aynı grant (yeni katalog modülü açılmaz). Tenant izolasyonu (§4b); firma + belge türü başına
// **tek** routing (upsert). `Result<T>` her durumda 200 zarf (§1); liste sayfalı (`value` + `total`).

import { api } from './client';
import type { DocumentTypeRef } from './documentTypes';
import type { Empty } from './envelope';

/**
 * Onay departmanı — §31 (`PermitApprovalDepartment`, PascalCase wire). Sabit küme: `Isg` (İSG —
 * isg-expert + isg-manager) · `Hekim` (İşyeri Hekimi — hekim rolü) · `Ik` (İnsan Kaynakları —
 * hr-expert). Departman→rol eşlemesi backend'dedir; FE değeri taşır + i18n ad (`permits.department.
 * <value>`) gösterir + rolden viewer departmanı türetir (bkz. `lib/departmentRoles`).
 */
export type PermitApprovalDepartment = 'Isg' | 'Hekim' | 'Ik';

/** Departman değerleri — seçim/filtre UI'ı için sabit sıra (İSG → Hekim → İK). */
export const PERMIT_APPROVAL_DEPARTMENTS: PermitApprovalDepartment[] = ['Isg', 'Hekim', 'Ik'];

/**
 * Bir belge türü için onay-routing kuralı — §31.2 (`PermitApprovalRoutingDto`). Kimlik
 * `(entity, code)` çiftidir (`DocumentTypeRef`; tam eşleşme). Kayıtta olmayan ya da pasif bir kod
 * satırı listede kalır (§27.0(6)). `requiredDepartments` = onay gereken departman(lar) (bir veya çok
 * — ör. `['Ik','Isg']`). `escalationHours` + `escalationDepartment` **hem-ya-hiç** (opsiyonel; süre
 * dolunca devralan tek departman). Sıralı iç `Id` sızmaz.
 */
export interface PermitApprovalRoutingDto extends DocumentTypeRef {
  uuid: string;
  /** Onay gereken departman(lar) — en az bir (§31.2). */
  requiredDepartments: PermitApprovalDepartment[];
  /** Eskalasyon süresi (saat) — verilmezse eskalasyon tanımsız. */
  escalationHours?: number | null;
  /** Süre dolunca devralan tek departman — verilmezse yok. */
  escalationDepartment?: PermitApprovalDepartment | null;
}

/**
 * Routing oluşturma isteği — §31.2 (upsert; aynı `(entity, code)` varsa günceller — yeni `201`,
 * güncelleme `200`). Eskalasyon **hem-ya-hiç** (biri verilip diğeri boşsa `400`); boş
 * `requiredDepartments` → `400`; `entity` `REQUIRED`/`INVALID`, `code` `REQUIRED`/`INVALID`/
 * `INACTIVE` (§27.6; pasif kod için YENİ satır) → `400`; `escalationHours <= 0` → `400`.
 */
export interface PermitApprovalRoutingCreateRequest extends DocumentTypeRef {
  requiredDepartments: PermitApprovalDepartment[];
  escalationHours?: number | null;
  escalationDepartment?: PermitApprovalDepartment | null;
}

/**
 * Routing güncelleme isteği — §31.2 (`PUT`; belge türü **sabit**, yalnız departman kümesi +
 * eskalasyon güncellenir). Doğrulama create ile aynı.
 */
export interface PermitApprovalRoutingUpdateRequest {
  requiredDepartments: PermitApprovalDepartment[];
  escalationHours?: number | null;
  escalationDepartment?: PermitApprovalDepartment | null;
}

/**
 * Routing liste parametreleri — §31.2 (`GET`; sayfalı + `entity` filtresi — tanınmayan değer süzgeci
 * uygulamaz). `page`/`pageSize` §3 paging.
 */
export interface PermitApprovalRoutingListParams {
  entity?: DocumentTypeRef['entity'];
  page?: number;
  pageSize?: number;
}

// ============================================================================
// §31.2 — Firma Onay-Routing CRUD (İSG uzmanı; tenant-scoped; `permit-types` grant)
// ============================================================================

/**
 * `GET /firm-settings/permit-approval-routing` (`list`) — firmanın tanımlı routing kurallarını
 * döndürür (sayfalı; opsiyonel `entity` filtresi). Ayarı olmayan belge türü LİSTEDE OLMAZ —
 * varsayılan "yalnız İSG" onayına düşer (§31.2). → `PermitApprovalRoutingDto[]` (+ total).
 */
export function listPermitApprovalRouting(params: PermitApprovalRoutingListParams = {}) {
  return api.list<PermitApprovalRoutingDto>('firm-settings/permit-approval-routing', {
    searchParams: {
      entity: params.entity,
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

/**
 * `GET /firm-settings/permit-approval-routing/{uuid}` (`get`) — tek routing. Yok →
 * `404 PERMIT_APPROVAL_ROUTING_NOT_FOUND`. → `PermitApprovalRoutingDto`.
 */
export function getPermitApprovalRouting(uuid: string) {
  return api.get<PermitApprovalRoutingDto>(`firm-settings/permit-approval-routing/${uuid}`);
}

/**
 * `POST /firm-settings/permit-approval-routing` (`create`) — **upsert**: aynı
 * `(entity, code)` varsa günceller (yeni `201`, güncelleme `200`). → oluşan/güncel satır.
 * Doğrulama hatası → `400`.
 */
export function createPermitApprovalRouting(body: PermitApprovalRoutingCreateRequest) {
  return api.post<PermitApprovalRoutingDto>('firm-settings/permit-approval-routing', {
    json: body,
  });
}

/**
 * `PUT /firm-settings/permit-approval-routing/{uuid}` (`update`) — departman kümesi + eskalasyon
 * güncellenir (belge türü sabit). Yok → `404`. → güncel satır.
 */
export function updatePermitApprovalRouting(
  uuid: string,
  body: PermitApprovalRoutingUpdateRequest,
) {
  return api.put<PermitApprovalRoutingDto>(`firm-settings/permit-approval-routing/${uuid}`, {
    json: body,
  });
}

/**
 * `DELETE /firm-settings/permit-approval-routing/{uuid}` (`delete`) — routing kuralını soft-delete
 * eder (belge türü varsayılan "yalnız İSG" onayına döner). Yok → `404`.
 */
export function deletePermitApprovalRouting(uuid: string) {
  return api.delete<Empty>(`firm-settings/permit-approval-routing/${uuid}`);
}
